import * as React from 'react';

import {
  ALTURA_CELULA,
  BASE,
  BORDA,
  CX,
  CY,
  LARGURA_CELULA,
  PONTA,
  planejarMedidor,
  type BlocoDoMedidor,
  type Papel,
} from '@/componentes/plano-do-medidor';
import { pontosHexagono } from '@/design/favo';
import { cn } from '@/lib/utils';

/* CelulaNivel — a célula que não encheu.
 *
 * O medidor do veredito. Um número solto ("faltam R$ 8.368,42") obriga o
 * leitor a montar a proporção na cabeça; aqui a proporção É o objeto: a borda
 * de cima é a meta, o mel é o que o preço de hoje alcança e a parte hachurada
 * é, literalmente, o que falta.
 *
 * Duas decisões de desenho que parecem detalhe e não são:
 *
 * 1. O RECIPIENTE É A CÉLULA DA MARCA, não um hexágono regular: ponta em
 *    cima, ponta embaixo, laterais RETAS. Num hexágono regular um nível de
 *    70% cai na parte que estreita, o menisco fica curto e o gráfico parece
 *    cheio. Com as laterais retas o menisco atravessa a largura inteira e a
 *    lâmina que falta abre na ponta, exatamente onde o olho sobe.
 * 2. O CHEIO É FEITO DE CÉLULAS e o vazio não é feito de nada: a textura de
 *    favo dentro do mel faz a diferença entre os dois virar diferença de
 *    MATÉRIA, não só de cor. Quem não distingue as duas cores ainda distingue
 *    liso de texturado.
 *
 * O componente não calcula nada: recebe a fração e os textos já formatados,
 * e onde cada texto cai (com que tinta e em que tamanho) é conta de
 * `plano-do-medidor.ts`. Aqui só se desenha. Quem sabe que "falta" é
 * `recomendacao.reajuste` é a tela. */

export type LinhaDeNivel = {
  rotulo: string;
  valor: string;
};

export type CelulaNivelProps = {
  /** Quanto da célula está cheio, de 0 a 1. Fora da faixa é grampeado. */
  fracao: number;
  /** O que a borda de cima representa. */
  topo?: LinhaDeNivel | undefined;
  /** O que falta para encher. */
  falta?: LinhaDeNivel | undefined;
  /** O que já está dentro. */
  nivel?: LinhaDeNivel | undefined;
  /** Uma linha sob a célula. Quebra sozinha se não couber na largura. */
  legenda?: string | undefined;
  /** Descrição para leitor de tela. Diga o número, não a forma. */
  descricao: string;
  largura?: number | undefined;
  className?: string | undefined;
};

const RAIO_CERA = 13;

const CLASSE: Record<Papel, string> = {
  rotulo: 'nivel-rotulo',
  valor: 'nivel-num',
  falta: 'nivel-falta',
  pct: 'nivel-pct',
};

/** A célula da marca, parametrizada para poder desenhar também a parede. */
function contornoCelula(l: number, a: number, p: number): string {
  return [
    `${CX},${CY - a / 2}`,
    `${CX + l / 2},${CY - a / 2 + p}`,
    `${CX + l / 2},${CY + a / 2 - p}`,
    `${CX},${CY + a / 2}`,
    `${CX - l / 2},${CY + a / 2 - p}`,
    `${CX - l / 2},${CY - a / 2 + p}`,
  ].join(' ');
}

function Bloco({ bloco }: { bloco: BlocoDoMedidor }) {
  return (
    <>
      {bloco.linhas.map((linha) => (
        <text
          key={`${linha.papel}-${linha.texto}`}
          x={CX}
          y={linha.y}
          fontSize={linha.tamanho}
          textAnchor="middle"
          className={CLASSE[linha.papel]}
          fill={bloco.tinta}
        >
          {linha.texto}
        </text>
      ))}
    </>
  );
}

export function CelulaNivel({
  fracao,
  topo,
  falta,
  nivel,
  legenda,
  descricao,
  largura = 300,
  className,
}: CelulaNivelProps) {
  // Um id por instância: dois medidores na mesma tela não podem disputar o
  // mesmo <clipPath>, ou o segundo recorta pelo do primeiro.
  const id = React.useId().replace(/:/g, '');
  const plano = planejarMedidor({
    fracao,
    ...(falta ? { falta: [falta.rotulo, falta.valor] as const } : {}),
    ...(nivel ? { nivel: [nivel.rotulo, nivel.valor] as const } : {}),
    ...(legenda ? { legenda } : {}),
  });

  const alturaNivel = plano.menisco;
  const larguraCera = Math.sqrt(3) * RAIO_CERA;

  return (
    <svg
      viewBox={`0 0 300 ${plano.altura}`}
      className={cn('h-auto w-full', className)}
      style={{ maxWidth: largura }}
      role="img"
      aria-label={descricao}
    >
      <defs>
        <clipPath id={`recorte-${id}`}>
          <polygon points={contornoCelula(LARGURA_CELULA, ALTURA_CELULA, PONTA)} />
        </clipPath>
        <linearGradient id={`mel-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--mel)" />
          <stop offset="1" stopColor="var(--mel-fundo)" />
        </linearGradient>
        <pattern
          id={`falta-${id}`}
          width="9"
          height="9"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="9" stroke="var(--negativo)" strokeOpacity=".40" strokeWidth="2" />
        </pattern>
        <pattern id={`cera-${id}`} width={larguraCera} height={3 * RAIO_CERA} patternUnits="userSpaceOnUse">
          {[
            [0, 0],
            [larguraCera, 0],
            [0, 3 * RAIO_CERA],
            [larguraCera, 3 * RAIO_CERA],
            [larguraCera / 2, 1.5 * RAIO_CERA],
          ].map(([hx, hy]) => (
            <polygon
              key={`${hx}-${hy}`}
              points={pontosHexagono(hx as number, hy as number, RAIO_CERA)}
              fill="none"
              stroke="var(--mel-cera)"
              strokeWidth=".9"
            />
          ))}
        </pattern>
      </defs>

      {/* A parede de cera: a célula vizinha que não está lá. */}
      <polygon
        points={contornoCelula(LARGURA_CELULA + 22, ALTURA_CELULA + 22, PONTA + 9)}
        fill="none"
        stroke="var(--fio-forte)"
        strokeWidth="1"
        strokeDasharray="3 7"
      />

      <g clipPath={`url(#recorte-${id})`}>
        <rect x="0" y="0" width="300" height={alturaNivel} fill="var(--negativo)" fillOpacity=".12" />
        <rect x="0" y="0" width="300" height={alturaNivel} fill={`url(#falta-${id})`} />
        <g className="nivel-sobe">
          <rect x="0" y={alturaNivel} width="300" height={BASE - alturaNivel} fill={`url(#mel-${id})`} />
          <rect x="0" y={alturaNivel} width="300" height={BASE - alturaNivel} fill={`url(#cera-${id})`} />
        </g>
      </g>

      {/* Menisco: a superfície do mel, na largura cheia da célula. Escuro, e
          não da cor da nata: sobre o mel a nata dá 1,7:1, e no laudo claro (em
          que a lâmina que falta também é clara) a linha sumia dos dois lados.
          Contra o mel o escuro dá 7:1 em qualquer tema. */}
      <line
        x1={CX - LARGURA_CELULA / 2}
        y1={alturaNivel}
        x2={CX + LARGURA_CELULA / 2}
        y2={alturaNivel}
        stroke="var(--mata)"
        strokeWidth="2.5"
      />

      <polygon
        points={contornoCelula(LARGURA_CELULA, ALTURA_CELULA, PONTA)}
        fill="none"
        stroke="var(--fio-forte)"
        strokeWidth="1.5"
      />

      {topo ? (
        <>
          <line
            x1={CX - LARGURA_CELULA / 2 - 22}
            y1={BORDA}
            x2={CX + LARGURA_CELULA / 2 + 22}
            y2={BORDA}
            stroke="var(--fio-forte)"
            strokeWidth="1"
          />
          <text x={CX} y={BORDA - 26} textAnchor="middle" fontSize="12" className="nivel-rotulo" fill="var(--tinta-2)">
            {topo.rotulo}
          </text>
          <text x={CX} y={BORDA - 8} textAnchor="middle" fontSize="16" className="nivel-num" fill="var(--tinta)">
            {topo.valor}
          </text>
        </>
      ) : null}

      {plano.falta ? <Bloco bloco={plano.falta} /> : null}
      <Bloco bloco={plano.nivel} />

      {plano.legenda.map((linha, i) => (
        <text
          key={linha}
          x={CX}
          y={BASE + 26 + i * 18}
          textAnchor="middle"
          fontSize="12"
          className="nivel-rotulo"
          fill="var(--tinta-2)"
        >
          {linha}
        </text>
      ))}
    </svg>
  );
}
